"use client";

import { useState, useEffect } from 'react';

interface FloatingElement {
  id: string;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
}

const floatingIcons: FloatingElement[] = [
  { id: 'icon-1', x: 7,  y: 18, size: 42, delay: 0,   duration: 9   },
  { id: 'icon-2', x: 87, y: 14, size: 36, delay: 1.4, duration: 11  },
  { id: 'icon-3', x: 14, y: 68, size: 38, delay: 2.2, duration: 8.5 },
  { id: 'icon-4', x: 82, y: 64, size: 44, delay: 0.7, duration: 10  },
];

const dataPoints: FloatingElement[] = [
  { id: 'dp-1', x: 22, y: 35, size: 8,  delay: 0,   duration: 3.4 },
  { id: 'dp-2', x: 76, y: 28, size: 7,  delay: 0.8, duration: 3.9 },
  { id: 'dp-3', x: 38, y: 62, size: 9,  delay: 1.4, duration: 4.2 },
  { id: 'dp-4', x: 64, y: 55, size: 7,  delay: 2.0, duration: 3.6 },
];

function TruckIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
      <rect x="4" y="18" width="28" height="18" rx="2" fill="#C5A572" opacity="0.18" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.45" />
      <path d="M32 24 L32 36 L46 36 L46 28 L40 24 Z" fill="#C5A572" opacity="0.15" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.4" />
      <circle cx="12" cy="37" r="4" fill="none" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.5" />
      <circle cx="38" cy="37" r="4" fill="none" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.5" />
      <line x1="4" y1="28" x2="32" y2="28" stroke="#C5A572" strokeWidth="0.8" strokeOpacity="0.25" />
    </svg>
  );
}

function GPSPinIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
      <path d="M25 6 C18 6 12 12 12 19 C12 28 25 44 25 44 C25 44 38 28 38 19 C38 12 32 6 25 6 Z" fill="#C5A572" opacity="0.15" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.45" />
      <circle cx="25" cy="19" r="5" fill="#C5A572" opacity="0.35" />
      <circle cx="25" cy="19" r="9" fill="none" stroke="#C5A572" strokeWidth="1" strokeOpacity="0.2" />
    </svg>
  );
}

function RouteIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" fill="none">
      <circle cx="12" cy="25" r="5" fill="#C5A572" opacity="0.25" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.45" />
      <circle cx="38" cy="25" r="5" fill="#C5A572" opacity="0.25" stroke="#C5A572" strokeWidth="1.5" strokeOpacity="0.45" />
      <path d="M17 25 Q25 14 33 25" stroke="#C5A572" strokeWidth="1.5" fill="none" strokeOpacity="0.4" strokeDasharray="3 2" />
      <circle cx="25" cy="18" r="3" fill="#C5A572" opacity="0.45" />
    </svg>
  );
}

const iconComponents = [TruckIcon, GPSPinIcon, RouteIcon, TruckIcon];

export default function HeroSection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 12,
        y: (e.clientY / window.innerHeight - 0.5) * 12,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative bg-navy pt-24 pb-14 px-4 min-h-[100svh] sm:pt-28 sm:pb-16 sm:px-6 md:pt-32 md:pb-20 md:min-h-screen flex items-center overflow-hidden">
      {/* Animated Background Gradients */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-[#0c1a2a]"></div>
        <div className="absolute top-1/3 right-1/4 w-[600px] h-[600px] bg-gold/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 left-1/5 w-[500px] h-[500px] bg-gold/8 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] bg-gold/4 rounded-full blur-2xl animate-pulse-slow" style={{ animationDelay: '0.8s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full min-w-0 max-w-5xl mx-auto text-center">
        {/* Minimal compliance motif — single horizontal rule + shield mark */}
        <div
          className="mb-8 sm:mb-10 flex items-center justify-center gap-4 sm:gap-5 max-w-md mx-auto"
          aria-hidden="true"
        >
          <span className="h-px flex-1 min-w-[2.5rem] bg-gradient-to-r from-transparent via-gold/25 to-gold/45" />
          <svg
            className="shrink-0 text-gold/75 w-7 h-7 sm:w-8 sm:h-8"
            viewBox="0 0 32 32"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.15"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 5.5l8.5 3.5v7.2c0 5.2-3.6 9.9-8.5 11.8-4.9-1.9-8.5-6.6-8.5-11.8V9L16 5.5z" />
            <path d="M11.5 15.5l3.2 3.2 6.3-6.3" />
          </svg>
          <span className="h-px flex-1 min-w-[2.5rem] bg-gradient-to-l from-transparent via-gold/25 to-gold/45" />
        </div>

        <h1 className="font-serif text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight sm:leading-[1.15] font-bold mb-8 animate-fade-in">
          Logistics Data Chaos Solved with Real-Time{' '}
          <span
            className="italic"
            style={{
              color: '#C5A572',
              textShadow: '0 0 20px rgba(197, 165, 114, 0.4), 0 0 40px rgba(197, 165, 114, 0.2)',
            }}
          >
            Shipment Visibility
          </span>
        </h1>
        <p className="text-white/90 text-base sm:text-lg md:text-xl leading-relaxed max-w-4xl mx-auto mb-12 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          Transform fragmented TMS, WMS, and GPS data into unified supply chain intelligence and operational excellence.
        </p>
        <button
          className="w-full max-w-sm sm:w-auto mx-auto sm:mx-0 bg-gold hover:bg-gold/90 text-navy font-bold text-base sm:text-lg px-6 sm:px-10 py-3.5 sm:py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 animate-fade-in"
          style={{ animationDelay: '0.4s' }}
        >
          Schedule Logistics Assessment →
        </button>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-18px) translateX(8px); }
          50% { transform: translateY(-10px) translateX(-8px); }
          75% { transform: translateY(-14px) translateX(4px); }
        }
        @keyframes route-flow {
          0% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 400; }
        }
        .animate-route-flow {
          stroke-dasharray: 12 6;
          animation: route-flow 4s linear infinite;
        }
        .animate-pulse-slow {
          animation: pulse 4.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </section>
  );
}